import { useMemo } from "react";

import {
  AdminPanel,
  AdminMiniList,
  AdminMiniRow,
  Banner,
} from "@/core/ui/molecules";
import { useService } from "@/core/utils/hooks";

import {
  getPlanAccessPresets,
  type PlanAccessPresetsQuery,
} from "@/app/plans/data_sources/services";
import type { APIPlanResolvedAccessPresetsType } from "@/core/data_sources/models/APIPlanDefinition";

export function ResolvedAccessPresetsTab({
  planId,
}: {
  readonly planId: string | number;
}) {
  const { data, loading } = useService(getPlanAccessPresets, {
    id: planId,
    page: 1,
    page_size: 1,
  } as PlanAccessPresetsQuery);

  const resolved = useMemo<APIPlanResolvedAccessPresetsType | null>(
    () => data?.data ?? null,
    [data],
  );

  if (loading && !resolved) {
    return (
      <p className="text-xs text-ink-muted">Loading resolved access presets…</p>
    );
  }

  if (!resolved) {
    return (
      <Banner tone="warning">
        This plan has not been published yet. Resolved access presets are only
        available after the first publish (current_version &gt; 0).
      </Banner>
    );
  }

  const presets = resolved.permission_profile_presets ?? [];
  const assignments = resolved.default_assignments ?? {};

  return (
    <div>
      <Banner tone="ok">
        Enriched snapshot from the published plan. Use this for Tenant Setup /
        runtime installation instead of parsing the raw config.
      </Banner>
      <AdminPanel className="p-[18px]">
        <h3 className="mb-3 text-[15px] font-extrabold text-ink">
          Resolved Permission Profile Presets
        </h3>
        <AdminMiniList>
          {presets.map((p) => (
            <AdminMiniRow key={p.preset_code}>
              <span>
                <strong>{p.name ?? p.preset_code}</strong>
                <span className="block text-xs text-ink-muted">
                  {p.preset_code} · v{p.preset_version ?? "?"} ·{" "}
                  {p._resolved
                    ? "resolved"
                    : "unresolved (removed from catalog)"}
                </span>
              </span>
              <span className="text-xs text-ink-muted">
                auto_install: {p.auto_install ? "yes" : "no"}
              </span>
            </AdminMiniRow>
          ))}
          {presets.length === 0 ? (
            <li className="px-0 py-2 text-xs text-ink-muted">
              No presets in snapshot.
            </li>
          ) : null}
        </AdminMiniList>
      </AdminPanel>
      <AdminPanel className="mt-3 p-[18px]">
        <h3 className="mb-3 text-[15px] font-extrabold text-ink">
          Default Assignments
        </h3>
        <AdminMiniList>
          {Object.entries(assignments).map(([key, a]) => (
            <AdminMiniRow key={key}>
              <span>
                <strong>{key}</strong>
                <span className="block text-xs text-ink-muted">
                  {a.role} · {a.permission_profile_preset_code} ·{" "}
                  {a.workspace_scope}
                  {a.approval_level ? ` · L${a.approval_level}` : ""}
                </span>
              </span>
            </AdminMiniRow>
          ))}
        </AdminMiniList>
      </AdminPanel>
    </div>
  );
}
